import type { CrewVesselListRow, ReliefBoardRow, CrewReplacementRow } from './types';

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** True if COE / intention / ext COE falls within the watch window (including overdue). */
export function needsRelief(row: CrewVesselListRow, withinDays: number, today = isoToday()): boolean {
  const until = addDaysIso(today, withinDays);
  const dates = [row.coeDate, row.intentionSignOffDate, row.extCoeDate].filter(
    (d): d is string => !!d,
  );
  if (!dates.length) return false;
  return dates.some((d) => d.slice(0, 10) <= until);
}

export function crewName(row: Pick<CrewVesselListRow, 'lastName' | 'firstName'>): string {
  return [row.lastName, row.firstName].filter(Boolean).join(' ');
}

export type BoardLine = {
  key: string;
  rankId: number | null;
  rankLabel: string;
  rankSortOrder: number;
  showRankLabel: boolean;
  onboard: CrewVesselListRow;
  replacement: CrewReplacementRow | null;
};

/** Merge onboard tours with open/completed replacements for the planner board (and print). */
export function buildBoardLines(
  onboard: CrewVesselListRow[],
  replacements: CrewReplacementRow[],
): BoardLine[] {
  const byTx = new Map(replacements.map((r) => [r.outgoingTransactionId, r]));
  const sorted = [...onboard].sort((a, b) => {
    const sa = a.rankSortOrder ?? 9999;
    const sb = b.rankSortOrder ?? 9999;
    if (sa !== sb) return sa - sb;
    return crewName(a).localeCompare(crewName(b));
  });

  let prevRankKey = '';
  const lines: BoardLine[] = sorted.map((row) => {
    const rankKey = row.rankId != null ? `r:${row.rankId}` : `n:${row.lastRank || '—'}`;
    const showRankLabel = rankKey !== prevRankKey;
    prevRankKey = rankKey;
    return {
      key: String(row.transactionId),
      rankId: row.rankId ?? null,
      rankLabel: row.lastRank || '—',
      rankSortOrder: row.rankSortOrder ?? 9999,
      showRankLabel,
      onboard: row,
      replacement: byTx.get(row.transactionId) ?? null,
    };
  });

  // Keep completed changes visible so S/Off + S/On can be undone.
  const usedTx = new Set(onboard.map((r) => r.transactionId));
  const completedOrphans = replacements
    .filter((r) => r.status === 'COMPLETED' && !usedTx.has(r.outgoingTransactionId))
    .sort((a, b) => {
      if (a.rankSortOrder !== b.rankSortOrder) return a.rankSortOrder - b.rankSortOrder;
      return a.outgoingCrewName.localeCompare(b.outgoingCrewName);
    });

  for (const r of completedOrphans) {
    const rankKey = r.rankId != null ? `r:${r.rankId}` : `n:${r.rank || '—'}`;
    const showRankLabel = rankKey !== prevRankKey;
    prevRankKey = rankKey;
    const nameParts = r.outgoingCrewName.trim().split(/\s+/);
    const lastName = nameParts[0] || r.outgoingCrewName;
    const firstName = nameParts.slice(1).join(' ') || '';
    lines.push({
      key: `completed:${r.id}`,
      rankId: r.rankId,
      rankLabel: r.rank || '—',
      rankSortOrder: r.rankSortOrder,
      showRankLabel,
      onboard: {
        transactionId: r.outgoingTransactionId,
        crewId: r.outgoingCrewId,
        lastName,
        firstName,
        lastRank: r.rank,
        rankId: r.rankId,
        rankSortOrder: r.rankSortOrder,
        status: 'OFB',
        coeDate: r.coeDate,
        intentionSignOffDate: r.intentionSignOffDate,
        signOnDate: r.signOnDate,
        signOffDate: r.plannedSignOffDate,
      } as CrewVesselListRow,
      replacement: r,
    });
  }

  return lines;
}

export function filterBoardLines(
  lines: BoardLine[],
  opts: { needsReliefOnly: boolean; withinDays: number },
): BoardLine[] {
  if (!opts.needsReliefOnly) return lines;
  return lines.filter(
    (l) => l.replacement?.status === 'COMPLETED' || needsRelief(l.onboard, opts.withinDays),
  );
}

export function replacementPlanPrintPath(filters: {
  vesselId: string;
  withinDays: string;
  needsReliefOnly: boolean;
}): string {
  const qs = new URLSearchParams({
    vesselId: filters.vesselId,
    withinDays: filters.withinDays || '90',
    needsReliefOnly: filters.needsReliefOnly ? '1' : '0',
  });
  return `/crew/replacement-planner/print?${qs.toString()}`;
}

export function getAssignWarnings(
  lineRankId: number | null,
  lineRankLabel: string,
  relief: ReliefBoardRow,
  today = isoToday(),
  matrixLines: string[] = [],
): string[] {
  const warnings: string[] = [];
  if (relief.notRecommended) warnings.push('Not recommended (NR)');
  if (relief.expiredCerts > 0) {
    warnings.push(`${relief.expiredCerts} expired certificate(s)`);
  }
  if (relief.medicalNextDue && relief.medicalNextDue.slice(0, 10) < today) {
    warnings.push(`Medical overdue (${relief.medicalNextDue.slice(0, 10)})`);
  }
  if (lineRankId != null && relief.rankId != null && lineRankId !== relief.rankId) {
    warnings.push(`Rank mismatch: ${relief.rank || '—'} → slot ${lineRankLabel}`);
  }
  for (const line of matrixLines) {
    if (line) warnings.push(line);
  }
  return warnings;
}

export type ConflictInfo = {
  duplicateRelief: Set<number>; // replacement ids
  overlapping: Set<number>;
};

function rangesOverlap(
  aStart: string | null,
  aEnd: string | null,
  bStart: string | null,
  bEnd: string | null,
): boolean {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
}

/** Same relief on 2+ open slots, or overlapping planned windows on same vessel+rank. */
export function detectConflicts(rows: CrewReplacementRow[]): ConflictInfo {
  const open = rows.filter((r) => r.status !== 'CANCELLED' && r.status !== 'COMPLETED');
  const duplicateRelief = new Set<number>();
  const overlapping = new Set<number>();

  const byIncoming = new Map<number, CrewReplacementRow[]>();
  for (const r of open) {
    if (r.incomingCrewId == null) continue;
    const list = byIncoming.get(r.incomingCrewId) ?? [];
    list.push(r);
    byIncoming.set(r.incomingCrewId, list);
  }
  for (const list of Array.from(byIncoming.values())) {
    if (list.length < 2) continue;
    for (const r of list) duplicateRelief.add(r.id);
  }

  for (let i = 0; i < open.length; i += 1) {
    for (let j = i + 1; j < open.length; j += 1) {
      const a = open[i];
      const b = open[j];
      if (a.vesselId !== b.vesselId) continue;
      if (a.rankId == null || b.rankId == null || a.rankId !== b.rankId) continue;
      if (a.id === b.id) continue;
      const a0 = a.plannedSignOffDate || a.plannedSignOnDate;
      const a1 = a.plannedSignOnDate || a.plannedSignOffDate;
      const b0 = b.plannedSignOffDate || b.plannedSignOnDate;
      const b1 = b.plannedSignOnDate || b.plannedSignOffDate;
      if (rangesOverlap(a0, a1, b0, b1)) {
        overlapping.add(a.id);
        overlapping.add(b.id);
      }
    }
  }

  return { duplicateRelief, overlapping };
}
